import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../utils/constants';
import ItemPresetInput from '../shared/ItemPresetInput';

const SepticAsBuiltSketchPreview = ({ selected, locked, unitLabel, setSelected }) => {
  const [dragRowId, setDragRowId] = useState(null);
  const [dragDraft, setDragDraft] = useState(null); // { rowId, x, y }
  const [viewBounds, setViewBounds] = useState(null); // frozen bounds used only during drag
  const svgRef = React.useRef(null);
  const activePointerRef = React.useRef(null);
  const activeTouchRef = React.useRef(null);
  const applyDragRef = React.useRef(null);
  // compass removed

  const symbolForLabel = (label) => {
    const l = (label || '').toLowerCase();
    if (l.includes('bed corner') || l.includes('field corner') || l.startsWith('corner')) return 'bed_corner';
    if (l.includes('d-box') || l.includes('dbox') || l.includes('distribution')) return 'dbox';
    if (l.includes('pump')) return 'pump';
    if (l.includes('cleanout')) return 'cleanout';
    if (l.includes('septic') && l.includes('lid')) return 'tank_lid';
    if (l.includes('lid')) return 'lid';
    return 'point';
  };

  const bedCornerOrder = (label) => {
    const l = (label || '').toLowerCase();
    const m = l.match(/(?:bed corner|field corner)\s*(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  };

  const triangulateDown = (ab, distA, distB) => {
    const d = Number(ab);
    const r0 = Number(distA);
    const r1 = Number(distB);
    if (!d || d <= 0 || Number.isNaN(r0) || Number.isNaN(r1)) return null;

    // Triangle inequality: |r0 - r1| <= d <= r0 + r1
    if (d < Math.abs(r0 - r1) - 1e-9 || d > (r0 + r1) + 1e-9) return null;

    const x = (r0 * r0 - r1 * r1 + d * d) / (2 * d);
    const ySq = r0 * r0 - x * x;
    if (ySq < -1e-6) return null;
    const y = Math.sqrt(Math.max(0, ySq));
    return { x, y };
  };

  const polygonOrderByAngle = (pts) => {
    if (!pts || pts.length < 3) return pts || [];
    const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
    return [...pts].sort((a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx));
  };


  const abRangeWarning = () => {
    const ab = selected?.reference_ab_distance;
    if (!ab || Number(ab) <= 0) return null;

    let lo = 0;
    let hi = Infinity;

    for (const r of (selected?.rows || [])) {
      const da = r?.dist_a;
      const db = r?.dist_b;
      if (da === null || da === undefined || db === null || db === undefined) continue;
      const d1 = Math.abs(Number(da) - Number(db));
      const d2 = Number(da) + Number(db);
      lo = Math.max(lo, d1);
      hi = Math.min(hi, d2);
    }

    if (!Number.isFinite(hi)) return null;

    const abNum = Number(ab);
    if (abNum < lo - 1e-6 || abNum > hi + 1e-6) {
      return { lo: Number(lo.toFixed(2)), hi: Number(hi.toFixed(2)) };
    }
    return null;
  };

  const labelPositions = (pts) => {
    // simple collision avoidance: push labels down if they overlap earlier labels
    const taken = [];
    const padX = 8;
    const padY = 4;
    const charW = 6.2; // rough
    const lineH = 14;

    const out = new Map();
    for (const p of pts) {
      const text = (p.isRef ? p.label : (p.label || '')).slice(0, 28);
      const w = Math.max(30, text.length * charW);
      const h = lineH;
      let x = p.sx + 10;
      let y = p.sy - 10;

      const intersects = (a, b) => !(a.x2 < b.x1 || a.x1 > b.x2 || a.y2 < b.y1 || a.y1 > b.y2);

      let box = { x1: x - padX, y1: y - h - padY, x2: x + w + padX, y2: y + padY };
      let guard = 0;
      while (taken.some((t) => intersects(box, t)) && guard < 30) {
        y += 14;
        box = { x1: x - padX, y1: y - h - padY, x2: x + w + padX, y2: y + padY };
        guard += 1;
      }
      taken.push(box);
      out.set(p.key, { x, y });
    }
    return out;
  };

  // Define refLabel function before it's used
  function refLabel(key) {
    const txt = key === 'A'
      ? (selected?.reference_point_a || '').trim()
      : (selected?.reference_point_b || '').trim();

    const label = txt ? `${key}: ${txt}` : key;
    // truncate to keep sketch clean
    return label.length > 44 ? `${label.slice(0, 44)}…` : label;
  }

  const computeData = () => {
    const ab = selected?.reference_ab_distance;
    if (!ab || Number(ab) <= 0) return null;

    const A = { key: 'A', label: refLabel('A'), isRef: true, x: 0, y: 0 };
    const B = { key: 'B', label: refLabel('B'), isRef: true, x: Number(ab), y: 0 };

    const rows = (selected?.rows || []).map((r) => {
      if (!r?.id) return null;

      // Draft override while dragging (not yet committed)
      if (dragDraft && dragDraft.rowId === r.id) {
        return { key: r.id, label: r.label || '', row: r, x: dragDraft.x, y: dragDraft.y };
      }

      // Saved overrides
      if (r?.sketch_x !== null && r?.sketch_x !== undefined && r?.sketch_y !== null && r?.sketch_y !== undefined) {
        return { key: r.id, label: r.label || '', row: r, x: Number(r.sketch_x), y: Math.max(0, Number(r.sketch_y)) };
      }

      // Auto
      if (r?.dist_a === null || r?.dist_a === undefined || r?.dist_b === null || r?.dist_b === undefined) return null;
      const p = triangulateDown(ab, r.dist_a, r.dist_b);
      if (!p) return null;
      return { key: r.id, label: r.label || '', row: r, x: p.x, y: p.y };
    }).filter(Boolean);

    const pts = [A, B, ...rows];
    const xs = pts.map((p) => p.x);
    const ys = pts.map((p) => p.y);

    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = 0;
    const maxY = Math.max(0, ...ys);

    return { ab: Number(ab), points: pts, bounds: { minX, maxX, minY, maxY } };
  };

  const setRowSketch = (rowId, x, y) => {
    if (!rowId) return;
    const ySafe = Math.max(0, Number(y));
    setSelected((prev) => ({
      ...prev,
      rows: (prev.rows || []).map((r) => (r.id === rowId ? { ...r, sketch_x: Number(x), sketch_y: ySafe } : r)),
    }));
  };

  const clearRowOverride = (rowId) => {
    if (!rowId) return;
    setSelected((prev) => ({
      ...prev,
      rows: (prev.rows || []).map((r) => (r.id === rowId ? { ...r, sketch_x: null, sketch_y: null } : r)),
    }));
  };

  const data = computeData();
  const warn = abRangeWarning();

  if (!selected) return null;
  if (!selected.reference_ab_distance) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-4">
        <div className="font-semibold text-gray-900">Sketch Preview</div>
        <div className="text-sm text-gray-600 mt-2">Add the A–B distance to generate a sketch preview.</div>
      </div>
    );
  }

  if (!data || !data.points || data.points.length < 2) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-4">
        <div className="font-semibold text-gray-900">Sketch Preview</div>
        <div className="text-sm text-gray-600 mt-2">Not enough data to generate the sketch yet.</div>
      </div>
    );
  }

  const W = 1100;
  const H = 850;
  const pad = 64;

  const bounds = dragRowId ? (viewBounds || data.bounds) : data.bounds;
  const spanX = Math.max(1e-6, bounds.maxX - bounds.minX);
  const spanY = Math.max(1e-6, bounds.maxY - bounds.minY);
  const scale = 0.94 * Math.min((W - 2 * pad) / spanX, (H - 2 * pad) / spanY);

  const toSvg = (x, y) => {
    const sx = pad + (x - bounds.minX) * scale;
    const sy = pad + (y - bounds.minY) * scale;
    return { sx, sy };
  };

  const points = data.points.map((p) => ({ ...p, ...toSvg(p.x, p.y) }));

  // Order corners by geometry so the polygon outlines cleanly (avoids self-crossing when numbering isn't perimeter-ordered)
  const cornerPts = points
    .filter((p) => !p.isRef)
    .map((p) => ({ p, order: bedCornerOrder(p.label) }))
    .filter((x) => x.order !== null)
    .map((x) => x.p);

  const orderedCorners = polygonOrderByAngle(cornerPts);

  const bedPath = orderedCorners.length >= 3
    ? `M ${orderedCorners.map((p) => `${p.sx} ${p.sy}`).join(' L ')} Z`
    : null;

  const labels = labelPositions(points);

  const startDrag = (point, { pointerId = null, touchId = null } = {}) => {
    if (!point?.row?.id) return;
    if (pointerId !== null) {
      activePointerRef.current = pointerId;
      activeTouchRef.current = null;
    }
    if (touchId !== null) {
      activeTouchRef.current = touchId;
      activePointerRef.current = null;
    }
    setViewBounds(data.bounds);
    setDragRowId(point.row.id);
    setDragDraft({ rowId: point.row.id, x: point.x, y: point.y });
  };

  const getNearestDraggablePoint = (clientX, clientY) => {
    const coords = getSvgCoordsFromClient(clientX, clientY);
    if (!coords) return null;
    const rect = svgRef.current?.getBoundingClientRect();
    const pxToSvg = rect?.width ? (W / rect.width) : 1;
    const threshold = 44 * pxToSvg;
    let nearest = null;
    let minDist = Infinity;
    points.forEach((p) => {
      if (p.isRef || !p.row?.id) return;
      const dist = Math.hypot(p.sx - coords.x, p.sy - coords.y);
      if (dist < minDist) {
        minDist = dist;
        nearest = p;
      }
    });
    return minDist <= threshold ? nearest : null;
  };

  const drawSymbol = (p) => {
    const sym = p.isRef ? 'ref' : symbolForLabel(p.label);
    const size = 10;
    if (sym === 'ref') {
      return <circle cx={p.sx} cy={p.sy} r={6} fill="white" stroke="#111827" strokeWidth={2} />;
    }
    if (sym === 'dbox') {
      return <rect x={p.sx - size / 2} y={p.sy - size / 2} width={size} height={size} fill="white" stroke="#111827" strokeWidth={2} />;
    }
    if (sym === 'tank_lid' || sym === 'lid') {
      return <circle cx={p.sx} cy={p.sy} r={size / 2} fill="white" stroke="#111827" strokeWidth={2} />;
    }
    if (sym === 'cleanout') {
      return (
        <g stroke="#111827" strokeWidth={2}>
          <line x1={p.sx - size / 2} y1={p.sy - size / 2} x2={p.sx + size / 2} y2={p.sy + size / 2} />
          <line x1={p.sx - size / 2} y1={p.sy + size / 2} x2={p.sx + size / 2} y2={p.sy - size / 2} />
        </g>
      );
    }
    if (sym === 'bed_corner') {
      return (
        <polygon
          points={`${p.sx},${p.sy - size / 2} ${p.sx - size / 2},${p.sy + size / 2} ${p.sx + size / 2},${p.sy + size / 2}`}
          fill="white"
          stroke="#111827"
          strokeWidth={2}
        />
      );
    }
    return <circle cx={p.sx} cy={p.sy} r={3} fill="#111827" />;
  };

  const getSvgCoordsFromClient = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const xRatio = (clientX - rect.left) / rect.width;
    const yRatio = (clientY - rect.top) / rect.height;
    return { x: xRatio * W, y: yRatio * H };
  };

  const getSvgCoords = (event) => getSvgCoordsFromClient(event.clientX, event.clientY);

  const applyDragFromSvgCoords = (mx, my) => {
    const x = bounds.minX + (mx - pad) / scale;
    const y = Math.max(0, bounds.minY + (my - pad) / scale);

    const x3 = Number(x.toFixed(3));
    const y3 = Number(y.toFixed(3));

    const ab = Number(selected?.reference_ab_distance || 0);
    if (ab > 0) {
      const distA = Math.sqrt(x3 * x3 + y3 * y3);
      const distB = Math.sqrt((x3 - ab) * (x3 - ab) + y3 * y3);
      const distA2 = Number(distA.toFixed(2));
      const distB2 = Number(distB.toFixed(2));

      setSelected((prev) => {
        const rows = (prev?.rows || []).map((r) => {
          if (r.id !== dragRowId) return r;
          return {
            ...r,
            dist_a_original: (r.dist_a_original ?? r.dist_a ?? null),
            dist_b_original: (r.dist_b_original ?? r.dist_b ?? null),
            dist_a: distA2,
            dist_b: distB2,
            sketch_x: null,
            sketch_y: null,
          };
        });
        return { ...prev, rows };
      });
    }

    setDragDraft({ rowId: dragRowId, x: x3, y: y3 });
  };

  const applyDragFromClient = (clientX, clientY) => {
    const coords = getSvgCoordsFromClient(clientX, clientY);
    if (!coords) return;
    applyDragFromSvgCoords(coords.x, coords.y);
  };

  applyDragRef.current = applyDragFromClient;

  const onPointerMove = (e) => {
    if (!dragRowId) return;
    if (activePointerRef.current !== null && activePointerRef.current !== e.pointerId) return;
    applyDragFromClient(e.clientX, e.clientY);
  };

  const onPointerUp = (e) => {
    if (activePointerRef.current !== null && activePointerRef.current !== e.pointerId) return;
    if (activePointerRef.current !== null && svgRef.current) {
      try {
        svgRef.current.releasePointerCapture(activePointerRef.current);
      } catch (err) {
        // no-op
      }
    }
    activePointerRef.current = null;
    activeTouchRef.current = null;
    // Measurements already updated live during drag.
    setDragRowId(null);
    setDragDraft(null);
    setViewBounds(null);
  };

  const onTouchMove = (e) => {
    e.preventDefault();
    if (!dragRowId) return;
    const touchList = Array.from(e.touches || []);
    const touch = touchList.find((t) => t.identifier === activeTouchRef.current) || touchList[0];
    if (!touch) return;
    applyDragFromClient(touch.clientX, touch.clientY);
  };

  const onTouchEnd = () => {
    activeTouchRef.current = null;
    setDragRowId(null);
    setDragDraft(null);
    setViewBounds(null);
  };

  useEffect(() => {
    if (!dragRowId) return;
    const handleMove = (event) => {
      const touchList = Array.from(event.touches || []);
      const touch = activeTouchRef.current !== null
        ? (touchList.find((t) => t.identifier === activeTouchRef.current) || touchList[0])
        : null;
      if (touch) {
        event.preventDefault();
        applyDragRef.current?.(touch.clientX, touch.clientY);
      }
    };
    const handlePointerMove = (event) => {
      if (activePointerRef.current !== null && activePointerRef.current === event.pointerId) {
        applyDragRef.current?.(event.clientX, event.clientY);
      }
    };
    const handleEnd = () => {
      activePointerRef.current = null;
      activeTouchRef.current = null;
      setDragRowId(null);
      setDragDraft(null);
      setViewBounds(null);
    };
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('touchend', handleEnd);
    window.addEventListener('touchcancel', handleEnd);
    window.addEventListener('pointerup', handleEnd);
    window.addEventListener('pointercancel', handleEnd);
    return () => {
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('touchend', handleEnd);
      window.removeEventListener('touchcancel', handleEnd);
      window.removeEventListener('pointerup', handleEnd);
      window.removeEventListener('pointercancel', handleEnd);
    };
  }, [dragRowId]);

  // compass removed

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-4" data-testid="septic-sketch-preview">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold text-gray-900" data-testid="septic-sketch-title">Sketch Preview</div>
          <div className="text-xs text-gray-500 mt-1" data-testid="septic-sketch-subtitle">Drag points to adjust. Points will not re-scale while dragging.</div>
        </div>

      </div>

      {warn ? (
        <div className="mt-3 mb-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900" data-testid="septic-sketch-ab-warning">
          <div className="font-semibold">A–B distance may not match the entered measurements</div>
          <div className="text-xs mt-1">
            Based on your A→Item and B→Item distances, A–B must be between <b>{warn.lo}</b> and <b>{warn.hi}</b> (triangle inequality).
            If A–B is outside this range, some points cannot be triangulated correctly.
          </div>
        </div>
      ) : null}

      <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden bg-gray-50" data-testid="septic-sketch-canvas" style={{ touchAction: 'none' }}>
        <div className="w-full" style={{ aspectRatio: '11 / 8.5', minHeight: 240, touchAction: 'none' }}>
          <svg
            ref={svgRef}
            width="100%"
            height="100%"
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="xMidYMid meet"
            onTouchStart={(e) => {
              if (dragRowId) return;
              const touch = e.touches?.[0];
              if (!touch) return;
              const nearest = getNearestDraggablePoint(touch.clientX, touch.clientY);
              if (!nearest) return;
              e.preventDefault();
              startDrag(nearest, { touchId: touch.identifier });
            }}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerLeave={onPointerUp}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onTouchCancel={onTouchEnd}
            data-testid="septic-sketch-svg"
            style={{ cursor: dragRowId ? 'grabbing' : 'default', touchAction: 'none' }}
          >

          {/* subtle scale label */}
          <text x={pad} y={H - 12} fontSize={10} fill="#6b7280">
            Scale: 1 unit = 1 {unitLabel}
          </text>

          {/* baseline */}
          {(() => {
            const a = points.find((p) => p.key === 'A');
            const b = points.find((p) => p.key === 'B');
            if (!a || !b) return null;
            return <line x1={a.sx} y1={a.sy} x2={b.sx} y2={b.sy} stroke="#6b7280" strokeWidth={2} />;
          })()}

          {/* distance guide lines (subtle): show A→point and B→point so the geometry feels anchored */}
          {(() => {
            const a = points.find((p) => p.key === 'A');
            const b = points.find((p) => p.key === 'B');
            if (!a || !b) return null;
            return (
              <g stroke="#cbd5e1" strokeWidth={1} strokeDasharray="4 4" opacity={0.35}>
                {points.filter((p) => !p.isRef).map((p) => (
                  <g key={`guides_${p.key}`}>
                    <line x1={a.sx} y1={a.sy} x2={p.sx} y2={p.sy} />
                    <line x1={b.sx} y1={b.sy} x2={p.sx} y2={p.sy} />
                  </g>
                ))}
              </g>
            );
          })()}

          {/* bed polygon */}
          {bedPath ? <path d={bedPath} fill="rgba(34,197,94,0.12)" stroke="#16a34a" strokeWidth={2} /> : null}

          {/* compass removed */}

          {/* points */}
          {points.map((p) => {
            const isDraggable = !locked && !p.isRef && p.row?.id;
            return (
              <g key={p.key} data-testid={`sketch-point-${p.key}`}>
                <g
                  onPointerDown={(e) => {
                    if (!isDraggable) return;
                    e.preventDefault();
                    e.stopPropagation();
                    if (svgRef.current) {
                      try {
                        svgRef.current.setPointerCapture(e.pointerId);
                      } catch (err) {
                        // no-op
                      }
                    }
                    startDrag(p, { pointerId: e.pointerId });
                  }}
                  onTouchStart={(e) => {
                    if (!isDraggable) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const touch = e.touches?.[0];
                    if (!touch) return;
                    startDrag(p, { touchId: touch.identifier });
                  }}
                  style={{ cursor: isDraggable ? 'grab' : 'default' }}
                >
                  {isDraggable && (
                    <circle cx={p.sx} cy={p.sy} r={28} fill="transparent" />
                  )}
                  {isDraggable && dragRowId === p.row?.id && (
                    <circle cx={p.sx} cy={p.sy} r={20} fill="rgba(59,130,246,0.15)" stroke="#3b82f6" strokeWidth={1.5} />
                  )}
                  {drawSymbol(p)}
                </g>
                {(() => {
                  const pos = labels.get(p.key) || { x: p.sx + 10, y: p.sy - 10 };
                  return (
                    <g>
                      {/* leader line */}
                      <line x1={p.sx + 3} y1={p.sy - 3} x2={pos.x - 4} y2={pos.y - 10} stroke="#9ca3af" strokeWidth={1} />
                      <text x={pos.x} y={pos.y} fontSize={12} fill="#111827">
                        {(p.isRef ? p.label : (p.label || '').slice(0, 28))}
                      </text>
                    </g>
                  );
                })()}
              </g>
            );
          })}
          </svg>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {(selected?.rows || []).filter((r) => r?.dist_a !== null && r?.dist_a !== undefined && r?.dist_b !== null && r?.dist_b !== undefined).map((r) => (
          <div key={r.id} className="border border-gray-200 rounded-lg p-3 bg-white">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-medium text-gray-900">{r.label || '(Unnamed item)'}</div>
                <div className="text-xs text-gray-500">A: {r.dist_a} • B: {r.dist_b}</div>
                {(r.dist_a_original !== null && r.dist_a_original !== undefined) || (r.dist_b_original !== null && r.dist_b_original !== undefined) ? (
                  <div className="text-[11px] text-gray-400 mt-1">Original: A: {r.dist_a_original ?? '-'} • B: {r.dist_b_original ?? '-'}</div>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={locked}
                  onClick={() => clearRowOverride(r.id)}
                  data-testid={`sketch-auto-button-${r.id}`}
                  className="px-2 py-1 text-xs rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  title="Clear manual override and return to auto-triangulation"
                >
                  Auto
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


const SepticAsBuiltModule = ({ onBack, currentUser }) => {
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'archive'
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [editorTab, setEditorTab] = useState('form'); // 'form' | 'sketch'

  const [mobilePane, setMobilePane] = useState('list'); // 'list' | 'editor' (mobile only)

  const [items, setItems] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);

  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 1024 : false;
  const [jobs, setJobs] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    linkMode: 'job', // 'job' | 'manual'
    job_id: '',
    job_label: '',
    customer_name: '',
    address: '',
    unit_system: 'imperial',
  });

  const [saveStatus, setSaveStatus] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);

  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfFilename, setPdfFilename] = useState('Septic_As_Built.pdf');

  const editorRef = React.useRef(null);
  const skipNextAutosaveRef = React.useRef(false);

  const loadJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs/active`);
      setJobs(res.data || []);
    } catch (e) {
      setJobs([]);
    }
  };

  const loadList = async () => {
    setListLoading(true);
    try {
      const res = await axios.get(`${API}/septic/asbuilts`);
      setItems(res.data || []);
    } catch (e) {
      setItems([]);
    } finally {
      setListLoading(false);
    }
  };

  const ASBUILT_ITEM_PRESETS = [
    'Septic Tank Lid 1',
    'Septic Tank Lid 2',
    'Pump Tank Lid',
    'D-Box Lid',
    'Septi-Surge Lid',
    'Field Corner 1',
    'Field Corner 2',
    'Field Corner 3',
    'Field Corner 4',
    'Header Outside Run 1',
    'Header Outside Run 2',
  ];


  const hasDuplicateLabels = (rows) => {
    const seen = new Set();
    for (const r of rows || []) {
      const key = (r?.label || '').trim().toLowerCase();
      if (!key) continue;
      if (seen.has(key)) return true;
      seen.add(key);
    }
    return false;
  };

  // Calculate duplicates reactively based on current selected rows
  const duplicatesPresent = React.useMemo(() => {
    return hasDuplicateLabels(selected?.rows || []);
  }, [selected?.rows]);

  const loadOne = async (id) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/septic/asbuilts/${id}`);
      const doc = res.data;

      setSelected(doc);
      setSelectedId(id);

      if (isMobile) {
        setMobilePane('editor');
      }

      // Ensure tab matches the record status (prevents confusion on mobile)
      const isArchived = doc?.status === 'archived' || doc?.is_completed;
      setActiveTab(isArchived ? 'archive' : 'active');

      // On mobile, bring the editor into view
      setTimeout(() => editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (e) {
      setSelected(null);
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
    loadList();
  }, []);

  const locked = Boolean(selected?.is_completed);

  // Autosave (debounced) for selected as-built
  useEffect(() => {
    if (!selectedId || !selected) return;
    if (locked) return;
    if (duplicatesPresent) {
      setSaveStatus('Fix duplicate item names to save');
      return;
    }

    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return;
    }

    setSaveStatus('Saving…');
    const t = setTimeout(async () => {
      try {
        // SepticAsBuiltUpdate model is strict (extra fields forbidden).
        // Build a safe payload with only the allowed editable fields.
        const payload = {
          job_id: selected.job_id ?? null,
          job_label: selected.job_label ?? null,
          customer_name: selected.customer_name,
          address: selected.address,
          legal_description: selected.legal_description ?? null,
          unit_system: selected.unit_system,
          reference_ab_distance: selected.reference_ab_distance ?? null,
          reference_point_a: selected.reference_point_a,
          reference_point_b: selected.reference_point_b,
          benchmark_name: selected.benchmark_name,
          benchmark_elevation: selected.benchmark_elevation ?? null,
          rows: selected.rows || [],
          is_completed: selected.is_completed,
        };

        const res = await axios.put(`${API}/septic/asbuilts/${selectedId}`, payload);
        skipNextAutosaveRef.current = true;
        setSelected(res.data);
        setSaveStatus('Saved');
      } catch (e) {
        setSaveStatus('Save failed');
      }
    }, 650);

    return () => clearTimeout(t);
  }, [selected, selectedId, locked]);

  const addRow = () => {
    if (!selected || selected?.is_completed) return;
    const next = {
      id: `row_${Date.now()}_${Math.random()}`,
      label: '',
      dist_a: null,
      dist_b: null,
      dist_c: null,
      elevation: null,
      sketch_x: null,
      sketch_y: null,
    };
    setSelected((prev) => ({ ...prev, rows: [...(prev.rows || []), next] }));
  };

  const deleteRow = (rowId) => {
    if (!selected || selected?.is_completed) return;
    setSelected((prev) => ({ ...prev, rows: (prev.rows || []).filter((r) => r.id !== rowId) }));
  };

  const updateRow = (rowId, patch) => {
    if (!selected || selected?.is_completed) return;
    setSelected((prev) => ({
      ...prev,
      rows: (prev.rows || []).map((r) => (r.id === rowId ? { ...r, ...patch } : r)),
    }));
  };

  const deleteAsBuilt = async (id) => {
    const ok = window.confirm('Delete this as built? This cannot be undone.');
    if (!ok) return;
    try {
      await axios.delete(`${API}/septic/asbuilts/${id}`);
      if (selectedId === id) {
        setSelected(null);
        setSelectedId(null);
      }
      loadList();
    } catch (e) {
      alert('Failed to delete.');
    }
  };

  const markCompleted = async () => {
    if (!selectedId || !selected) return;
    if (duplicatesPresent) {
      alert('Duplicate item names found in the table. Please make each item name unique before completing.');
      return;
    }
    if (!selected.reference_point_a?.trim() || !selected.reference_point_b?.trim()) {
      alert('Reference Points A and B are required before completing.');
      return;
    }
    if (!selected.reference_ab_distance || Number(selected.reference_ab_distance) <= 0) {
      alert('A–B Distance is required before completing (used for the site sketch).');
      return;
    }
    if (!selected.benchmark_name?.trim() || selected.benchmark_elevation === null || selected.benchmark_elevation === undefined || selected.benchmark_elevation === '') {
      alert('Benchmark name and benchmark elevation are required before completing.');
      return;
    }

    const ok = window.confirm('Mark this as built as COMPLETE? It will be archived and locked.');
    if (!ok) return;

    try {
      const res = await axios.put(`${API}/septic/asbuilts/${selectedId}`, { is_completed: true });
      setSelected(res.data);
      loadList();
      setActiveTab('archive');
    } catch (e) {
      alert('Failed to complete.');
    }
  };

  const reopen = async () => {
    if (!selectedId) return;
    const ok = window.confirm('Re-open this as built for editing?');
    if (!ok) return;
    try {
      const res = await axios.put(`${API}/septic/asbuilts/${selectedId}`, { is_completed: false });
      setSelected(res.data);
      loadList();
      setActiveTab('active');
    } catch (e) {
      alert('Failed to re-open.');
    }
  };

  const closePdf = () => {
    setPdfOpen(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
  };

  const viewPdf = async () => {
    if (!selectedId) return;
    setPdfLoading(true);
    try {
      const res = await axios.get(`${API}/septic/asbuilts/${selectedId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      const unitSafeCustomer = (selected?.customer_name || 'customer').replace(/[^a-z0-9 _-]/gi, '').trim().replace(/\s+/g, '_');
      const filename = `Septic_As_Built_${unitSafeCustomer}_${(selectedId || '').slice(0, 8)}.pdf`;

      setPdfFilename(filename);
      setPdfUrl(url);
      setPdfOpen(true);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const createNew = async () => {
    if (!createForm.customer_name.trim() || !createForm.address.trim()) {
      alert('Customer name and address are required.');
      return;
    }
    let job_id = null;
    let job_label = null;
    if (createForm.linkMode === 'job' && createForm.job_id) {
      const j = (jobs || []).find((x) => x.id === createForm.job_id);
      job_id = createForm.job_id;
      job_label = j ? `${j.customer_name} - ${j.job_location}` : createForm.job_label;
    } else if (createForm.linkMode === 'manual' && createForm.job_label.trim()) {
      job_label = createForm.job_label.trim();
    }

    try {
      const res = await axios.post(`${API}/septic/asbuilts`, {
        job_id,
        job_label,
        customer_name: createForm.customer_name.trim(),
        address: createForm.address.trim(),
        unit_system: createForm.unit_system,
      });
      setCreateOpen(false);
      setCreateForm({ linkMode: 'job', job_id: '', job_label: '', customer_name: '', address: '', unit_system: 'imperial' });

      // Always return to Active after creating a draft
      setActiveTab('active');
      if (isMobile) {
        setMobilePane('editor');
      }
      await loadList();
      await loadOne(res.data.id);
    } catch (e) {
      alert('Failed to create.');
    }
  };

  const filtered = (items || []).filter((x) => {
    const isArchived = x.status === 'archived' || x.is_completed;
    return activeTab === 'archive' ? isArchived : !isArchived;
  });

  const normalizedUnitSystem = (selected?.unit_system === 'cm') ? 'metric' : (selected?.unit_system === 'ft') ? 'imperial' : (selected?.unit_system || 'imperial');


  const unitLabel = (normalizedUnitSystem === 'metric') ? 'm' : 'ft';
  const unitDetail = (normalizedUnitSystem === 'metric') ? 'm / cm' : 'ft / inches';
  const includeC = false; // Reference Point C removed from UI (legacy data ignored)

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium">← Back to Production</button>
          <div className="text-right">
            <div className="text-sm text-gray-600">{saveStatus}</div>
          </div>
        </div>

        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900">Septic As Built</h1>
          <p className="text-base md:text-lg text-gray-600 mt-2">Create triangulation measurements and elevations for health unit records.</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left: list */}
          <div className={`w-full lg:w-1/3 ${isMobile && mobilePane === 'editor' ? 'hidden' : ''}`}>
            <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold text-gray-900">As Builts</div>
                <div className="flex items-center gap-2">
                  {isMobile && mobilePane === 'editor' ? (
                    <button
                      onClick={() => setMobilePane('list')}
                      className="px-3 py-2 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm"
                    >
                      Close
                    </button>
                  ) : null}
                  <button
                    onClick={() => setCreateOpen(true)}
                    className="px-3 py-2 rounded-md bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium"
                  >
                    + New
                  </button>
                </div>
              </div>

              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setActiveTab('active')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border ${activeTab === 'active' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                >
                  Active
                </button>
                <button
                  onClick={() => setActiveTab('archive')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border ${activeTab === 'archive' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                >
                  Archive
                </button>
              </div>

              {listLoading ? (
                <div className="text-sm text-gray-600">Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="text-sm text-gray-600">No as builts found.</div>
              ) : (
                <div className="space-y-2 max-h-[70vh] overflow-auto pr-1">
                  {filtered.map((x) => (
                    <div
                      key={x.id}
                      className={`border rounded-lg p-3 cursor-pointer ${selectedId === x.id ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:bg-gray-50'}`}
                      onClick={() => loadOne(x.id)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-semibold text-gray-900 text-sm">{x.customer_name}</div>
                          <div className="text-xs text-gray-600">{x.address}</div>
                          {x.job_label ? <div className="text-xs text-gray-500 mt-1">Job: {x.job_label}</div> : null}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${x.is_completed ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                            {x.is_completed ? 'Archived' : 'Draft'}
                          </span>
                          {currentUser?.rawRole === 'admin' ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteAsBuilt(x.id); }}
                              className="text-xs text-red-600 hover:text-red-700"
                            >
                              Delete
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: editor */}
          <div className={`w-full lg:w-2/3 ${isMobile && mobilePane === 'list' ? 'hidden' : ''}`} ref={editorRef}>
            {detailLoading ? (
              <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-8 text-center text-gray-600">Loading…</div>
            ) : !selected ? (
              <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-8 text-center text-gray-600">
                Select an as built to begin.
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6">
                {isMobile && mobilePane === 'editor' ? (
                  <div className="mb-4">
                    <button
                      onClick={() => setMobilePane('list')}
                      className="px-3 py-2 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm"
                    >
                      Close
                    </button>
                  </div>
                ) : null}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm text-gray-500">{locked ? 'Archived (locked)' : 'Draft (auto-saving)'}</div>
                    <h2 className="text-xl font-semibold text-gray-900">{selected.customer_name}</h2>
                    <div className="text-sm text-gray-600">{selected.address}</div>
                    {selected.job_label ? <div className="text-sm text-gray-500 mt-1">Job: {selected.job_label}</div> : null}
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    {locked ? (
                      <>
                        <button onClick={viewPdf} disabled={pdfLoading} className="px-3 py-2 rounded-md bg-gray-900 hover:bg-black text-white text-sm font-medium disabled:opacity-50">
                          {pdfLoading ? 'Opening…' : 'View PDF'}
                        </button>
                        <button onClick={reopen} className="px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">
                          Re-open
                        </button>
                      </>
                    ) : (
                      <button onClick={markCompleted} className="px-3 py-2 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-medium">
                        Mark Complete
                      </button>
                    )}
                  </div>
                </div>

                <div className="mb-4 flex items-center gap-2">
                  <button
                    onClick={() => setEditorTab('form')}
                    className={`px-3 py-2 rounded-md text-sm font-medium border ${editorTab === 'form' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                  >
                    Form
                  </button>
                  <button
                    onClick={() => setEditorTab('sketch')}
                    className={`px-3 py-2 rounded-md text-sm font-medium border ${editorTab === 'sketch' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                  >
                    Sketch
                  </button>
                </div>

                {editorTab === 'sketch' ? (
                  <div className="mb-6">
                    <SepticAsBuiltSketchPreview
                      selected={selected}
                      locked={locked}
                      unitLabel={unitLabel}
                      setSelected={setSelected}
                    />
                  </div>
                ) : null}

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                    <input
                      value={selected.customer_name || ''}
                      disabled={locked}
                      onChange={(e) => setSelected((prev) => ({ ...prev, customer_name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      value={selected.address || ''}
                      disabled={locked}
                      onChange={(e) => setSelected((prev) => ({ ...prev, address: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Legal Description</label>
                    <input
                      value={selected.legal_description || ''}
                      disabled={locked}
                      onChange={(e) => setSelected((prev) => ({ ...prev, legal_description: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                      placeholder="Optional"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Units ({unitDetail})</label>
                    <select
                      value={normalizedUnitSystem}
                      disabled={locked}
                      onChange={(e) => setSelected((prev) => ({ ...prev, unit_system: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                    >
                      <option value="imperial">Imperial (ft / inches)</option>
                      <option value="metric">Metric (m / cm)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Link (optional)</label>
                    <input
                      value={selected.job_label || ''}
                      disabled={locked}
                      onChange={(e) => setSelected((prev) => ({ ...prev, job_label: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                      placeholder="e.g., Smith - 123 Main St"
                    />
                    <div className="text-xs text-gray-500 mt-1">You can link to a job via the New dialog, or type a label here.</div>
                  </div>
                </div>

                <div className="mt-8">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">Reference Points</h3>
                    <span className="text-xs text-gray-500">A + B required.</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reference Point A</label>
                      <input
                        value={selected.reference_point_a || ''}
                        disabled={locked}
                        onChange={(e) => setSelected((prev) => ({ ...prev, reference_point_a: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                        placeholder="e.g., Front-left corner of house"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reference Point B</label>
                      <input
                        value={selected.reference_point_b || ''}
                        disabled={locked}
                        onChange={(e) => setSelected((prev) => ({ ...prev, reference_point_b: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                        placeholder="e.g., Front-right corner of house"
                      />
                    </div>
                    {/* Reference Point C removed (legacy field ignored) */}
                  </div>
                </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Distance between Ref A and Ref B (decimal {unitLabel})</label>
                      <input
                        type="number"
                        value={selected.reference_ab_distance ?? ''}
                        disabled={locked}
                        onChange={(e) => setSelected((prev) => ({ ...prev, reference_ab_distance: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                        placeholder={`e.g., ${unitLabel === 'ft' ? '20.0' : '6.0'}`}
                      />
                      <div className="text-xs text-gray-500 mt-1">Used as the baseline for the sketch.</div>
                    </div>


                <div className="mt-8">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">Benchmark</h3>
                    <span className="text-xs text-gray-500">Required to complete</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Benchmark Description</label>
                      <input
                        value={selected.benchmark_name || ''}
                        disabled={locked}
                        onChange={(e) => setSelected((prev) => ({ ...prev, benchmark_name: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                        placeholder="e.g., Top of foundation"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Benchmark Elevation (decimal {unitLabel})</label>
                  <div className="text-xs text-gray-500 mt-1">If Imperial, enter decimal feet. If Metric, enter decimal meters.</div>
                      <input
                        type="number"
                        value={selected.benchmark_elevation ?? ''}
                        disabled={locked}
                        onChange={(e) => setSelected((prev) => ({ ...prev, benchmark_elevation: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                        placeholder="e.g., 100.00"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-gray-900">Table</h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={addRow}
                        disabled={locked}
                        className="px-3 py-2 rounded-md bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium disabled:opacity-50"
                      >
                        + Add Row
                      </button>
                    </div>
                  </div>

                  <div className="overflow-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-900 text-white">
                        <tr>
                          <th className="text-left px-3 py-2">Item</th>
                          <th className="text-center px-3 py-2">A → Item ({unitLabel})</th>
                          <th className="text-center px-3 py-2">B → Item ({unitLabel})</th>
                          {includeC ? <th className="text-center px-3 py-2">C → Item ({unitLabel})</th> : null}
                          <th className="text-center px-3 py-2">Elevation vs Benchmark ({unitLabel})</th>
                          <th className="text-right px-3 py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selected.rows || []).length === 0 ? (
                          <tr>
                            <td colSpan={includeC ? 6 : 5} className="px-3 py-6 text-center text-gray-500">No rows yet. Add your first component.</td>
                          </tr>
                        ) : (
                          (selected.rows || []).map((r) => (
                            <tr key={r.id} className="border-t border-gray-200">
                              <td className="px-3 py-2 min-w-[260px]">
                                <ItemPresetInput
                                  value={r.label || ''}
                                  disabled={locked}
                                  presets={ASBUILT_ITEM_PRESETS}
                                  onChange={(next) => updateRow(r.id, { label: next })}
                                />
                              </td>
                              <td className="px-3 py-2 min-w-[140px]">
                                <input
                                  type="number"
                                  value={r.dist_a ?? ''}
                                  disabled={locked}
                                  onChange={(e) => updateRow(r.id, { dist_a: e.target.value === '' ? null : parseFloat(e.target.value), sketch_x: null, sketch_y: null, dist_a_original: null, dist_b_original: null })}
                                  className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-center disabled:bg-gray-100"
                                />
                              </td>
                              <td className="px-3 py-2 min-w-[140px]">
                                <input
                                  type="number"
                                  value={r.dist_b ?? ''}
                                  disabled={locked}
                                  onChange={(e) => updateRow(r.id, { dist_b: e.target.value === '' ? null : parseFloat(e.target.value), sketch_x: null, sketch_y: null, dist_a_original: null, dist_b_original: null })}
                                  className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-center disabled:bg-gray-100"
                                />
                              </td>
                              {includeC ? (
                                <td className="px-3 py-2 min-w-[140px]">
                                  <input
                                    type="number"
                                    value={r.dist_c ?? ''}
                                    disabled={locked}
                                    onChange={(e) => updateRow(r.id, { dist_c: e.target.value === '' ? null : parseFloat(e.target.value) })}
                                    className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-center disabled:bg-gray-100"
                                  />
                                </td>
                              ) : null}
                              <td className="px-3 py-2 min-w-[160px]">
                                <input
                                  type="number"
                                  value={r.elevation ?? ''}
                                  disabled={locked}
                                  onChange={(e) => updateRow(r.id, { elevation: e.target.value === '' ? null : parseFloat(e.target.value) })}
                                  className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-center disabled:bg-gray-100"
                                />
                              </td>
                              <td className="px-3 py-2 text-right min-w-[90px]">
                                <button
                                  disabled={locked}
                                  onClick={() => deleteRow(r.id)}
                                  className="text-red-600 hover:text-red-700 text-sm disabled:opacity-50"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-3 text-xs text-gray-500">
                    Tip: Use the Sketch tab to adjust point placement—distances update live.
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>

        {/* Create modal */}
        {createOpen ? (
          <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center z-[80] p-4 pt-12 sm:pt-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-2xl p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">New Septic As Built</h2>
                  <p className="text-sm text-gray-600">Create a draft as-built record. It will auto-save as you enter measurements.</p>
                </div>
                <button onClick={() => setCreateOpen(false)} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">×</button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                  <input
                    value={createForm.customer_name}
                    onChange={(e) => setCreateForm((p) => ({ ...p, customer_name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                  <input
                    value={createForm.address}
                    onChange={(e) => setCreateForm((p) => ({ ...p, address: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Units</label>
                  <select
                    value={createForm.unit_system}
                    onChange={(e) => setCreateForm((p) => ({ ...p, unit_system: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="imperial">Imperial (ft / inches)</option>
                    <option value="metric">Metric (m / cm)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link to Job (optional)</label>
                  <div className="flex gap-2 mb-2">
                    <button
                      onClick={() => setCreateForm((p) => ({ ...p, linkMode: 'job' }))}
                      className={`px-3 py-2 rounded-md text-sm font-medium border ${createForm.linkMode === 'job' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                    >
                      Choose Job
                    </button>
                    <button
                      onClick={() => setCreateForm((p) => ({ ...p, linkMode: 'manual' }))}
                      className={`px-3 py-2 rounded-md text-sm font-medium border ${createForm.linkMode === 'manual' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                    >
                      Manual
                    </button>
                  </div>

                  {createForm.linkMode === 'job' ? (
                    <select
                      value={createForm.job_id}
                      onChange={(e) => {
                        const jobId = e.target.value;
                        const j = (jobs || []).find((x) => x.id === jobId);
                        setCreateForm((p) => ({
                          ...p,
                          job_id: jobId,
                          customer_name: j?.customer_name || '',
                          address: j?.job_location || '',
                        }));
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >

                      <option value="">-- Select a Job --</option>
                      {(jobs || []).map((j) => (
                        <option key={j.id} value={j.id}>{j.customer_name} - {j.job_location}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={createForm.job_label}
                      onChange={(e) => setCreateForm((p) => ({ ...p, job_label: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Job label (optional)"
                    />
                  )}
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-2">
                <button
                  onClick={() => setCreateOpen(false)}
                  className="px-4 py-2 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={createNew}
                  className="px-4 py-2 rounded-md bg-orange-600 hover:bg-orange-700 text-white font-medium"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>


        {/* PDF modal (works on mobile + desktop) */}
        {pdfOpen ? (
          <div className="fixed inset-0 bg-black/60 flex items-start sm:items-center justify-center z-[90] p-4 pt-12 sm:pt-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-5xl h-[85vh] flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <div className="font-semibold text-gray-900 truncate pr-4">{pdfFilename}</div>
                <div className="flex items-center gap-2">
                  {pdfUrl ? (
                    <a
                      href={pdfUrl}
                      download={pdfFilename}
                      className="px-3 py-2 rounded-md bg-gray-900 hover:bg-black text-white text-sm font-medium"
                    >
                      Download
                    </a>
                  ) : null}
                  <button
                    onClick={closePdf}
                    className="px-3 py-2 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="flex-1 bg-gray-50">
                {pdfUrl ? (
                  <object data={pdfUrl} type="application/pdf" className="w-full h-full">
                    <iframe title="Septic As Built PDF" src={pdfUrl} className="w-full h-full" />
                  </object>
                ) : (
                  <div className="p-6 text-center text-gray-600">PDF unavailable</div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
  );
};



export default SepticAsBuiltModule;
